"use client";

import React from "react";
import { Check, X, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const comparisonFeatures = [{
  id: 1,
  feature: "Strategic Foundation",
  traditional: "Surface-Level Tactics",
  flavr: "Data-Driven Architecture",
  isCheck: false
}, {
  id: 2,
  feature: "Market Analysis Depth",
  traditional: "Standard Industry Reports",
  flavr: "CHECK",
  isCheck: true
}, {
  id: 3,
  feature: "Campaign ROI Tracking",
  traditional: "Estimated Projections",
  flavr: "CHECK",
  isCheck: true
}, {
  id: 4,
  feature: "Brand Positioning",
  traditional: "Generic Templates",
  flavr: "Elite Market Authority",
  isCheck: false
}, {
  id: 5,
  feature: "Execution Speed",
  traditional: "Fragmented Timelines",
  flavr: "High-Velocity Precision",
  isCheck: false
}, {
  id: 6,
  feature: "Competitive Edge",
  traditional: "Market Average",
  flavr: "2x Performance ROI",
  isCheck: false
}];
export default function TraditionalVsFlavr() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224594" id="the-performance-gap" className="py-24 dark bg-background text-foreground relative overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full opacity-20 pointer-events-none">
        <div className="absolute top-20 left-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground mb-6 tracking-tighter">
            The Performance Gap
          </h2>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            Standard market approaches often fall short of executive expectations. 
            Benchmark Media Consulting bridges the divide with architectural precision and data-driven strategy.
          </p>
        </div>

        <div className={`max-w-5xl mx-auto transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="bg-white/5 backdrop-blur-sm rounded-lg shadow-2xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/50">
                <TableRow className="hover:bg-transparent border-b-border">
                  <TableHead className="w-1/3 py-6 px-6 text-left align-bottom">
                    <span className="text-xs font-mono font-semibold text-muted-foreground uppercase tracking-widest">
                      Metric Analysis
                    </span>
                  </TableHead>
                  <TableHead className="w-1/3 py-6 px-6 text-center align-bottom">
                    <span className="text-xl font-default font-bold text-muted-foreground block mb-1">
                      Standard Market
                    </span>
                    <span className="text-xs font-mono text-muted-foreground/60 font-normal uppercase">
                      Low-Impact Visuals
                    </span>
                  </TableHead>
                  <TableHead className="w-1/3 py-6 px-6 text-center align-bottom border-x-2 border-t-2 border-primary bg-primary/10 relative">
                    {}
                    <div className="absolute top-0 left-0 w-full h-1 bg-primary shadow-[0_0_15px_rgba(45,91,255,0.5)]" />
                    <span className="text-2xl font-default font-black text-primary block mb-1">
                      BENCHMARK
                    </span>
                    <span className="text-xs font-mono text-primary/80 font-medium uppercase">
                      The Gold Standard
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((item, index) => {
                const isLast = index === comparisonFeatures.length - 1;
                return <TableRow key={item.id} data-index={index} className="hover:bg-white/5 transition-all duration-300 border-b-border group">
                      <TableCell className="py-5 px-6 font-medium text-foreground text-base font-default">
                        {item.feature}
                      </TableCell>
                      <TableCell className="py-5 px-6 text-center text-muted-foreground font-mono text-sm">
                        {item.traditional}
                      </TableCell>
                      <TableCell className={`py-5 px-6 text-center border-x-2 border-primary bg-primary/5 transition-transform duration-300 group-hover:scale-[1.01] ${isLast ? "border-b-2 rounded-b-none" : ""}`}>
                        {item.flavr === "CHECK" ? <div className="flex justify-center items-center">
                            <div className="bg-primary/20 p-1.5 rounded-sm">
                              <Check className="w-5 h-5 text-primary stroke-[3]" />
                            </div>
                          </div> : <span className="font-bold text-foreground font-default tracking-tight">
                            {item.flavr}
                          </span>}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-sm px-8 h-14 text-base bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(45,91,255,0.3)] transition-all">
              <Link to="/ConnectPage">
                Secure Your Strategy
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-sm px-8 h-14 text-base border-white/10 hover:bg-white/5 font-mono">
              <Link to="/MethodPage">
                The Methodology
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}